"use client";

import React from "react";
import { Wrench, Settings, Anchor, Hammer, Construction, Layers, Sparkles, Plus, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  name: "Process Piping",
  description: "Complete process piping solutions for industrial and manufacturing facilities.",
  bulletPoints: "Process Piping, High-Pressure Systems, Stainless Steel, Carbon Steel, Orbital Welding",
  icon: Wrench
}, {
  name: "Millwright",
  description: "Precision mechanical services for the installation and maintenance of industrial machinery.",
  bulletPoints: "Equipment Setting, Precision Alignment, Conveyor Systems, Pump Installation, Gearbox Repair",
  icon: Settings
}, {
  name: "Rigging",
  description: "Specialized heavy lifting and machinery moving services for complex industrial equipment.",
  bulletPoints: "Heavy Lifting, Machinery Moving, Crane Services, Specialized Transport",
  icon: Anchor
}, {
  name: "Fabrication",
  description: "Custom metal fabrication services utilizing advanced technology and expert craftsmanship.",
  bulletPoints: "Structural Steel, Custom Brackets, Platforms & Walkways, Sheet Metal, CAD Design",
  icon: Hammer
}, {
  name: "Construction and Maintenance",
  description: "Full-service industrial construction and ongoing facility maintenance programs.",
  bulletPoints: "Facility Upgrades, Structural Repairs, Preventive Maintenance, General Contracting, Turnkey Projects",
  icon: Construction
}, {
  name: "Floor Preparation & Floor Marking",
  description: "Professional surface preparation and safety marking for organized industrial spaces.",
  bulletPoints: "Shot Blasting, Safety Striping, Aisle Marking, Surface Leveling, Epoxy Coatings",
  icon: Layers
}, {
  name: "Polished Concrete Flooring",
  description: "High-performance industrial flooring with superior durability and aesthetics.",
  bulletPoints: "Surface Grinding, Honing, Polishing, Sealing & Protection, Decorative Finishes",
  icon: Sparkles
}, {
  name: "Additional Service",
  description: "Versatile industrial support services tailored to meet unique project requirements.",
  bulletPoints: "Demolition, Equipment Relocation, Emergency Repairs, Project Management, Safety Consulting",
  icon: Plus
}];
export default function FabricationServices() {
  const {
    ref: containerRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="fabrication-services" className="py-20 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={containerRef} className="flex flex-col gap-20">
          
          <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="max-w-4xl">
              <span className="text-secondary font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
                Explore Our Services
              </span>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-foreground mb-8 leading-[0.9]">
                INDUSTRIAL <br />
                <span className="text-primary">CAPABILITIES</span>
              </h2>
              <div className="h-1 w-24 bg-secondary mb-8 rounded-lg" />
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-2xl">
                FABCO Industrial Construction LLC delivers precision-engineered solutions across the industrial spectrum, from complex piping to heavy machinery relocation.
              </p>
            </div>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            {services.map((service, i) => {
            const Icon = service.icon;
            return <div key={i} data-index={i} className="group relative flex flex-col gap-6 p-8 rounded-lg bg-card border border-border hover:border-secondary/50 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                  <div className="size-14 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-500">
                    <Icon className="size-7" />
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-2xl font-bold tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                      {service.name}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  <div className="pt-4 mt-auto border-t border-border/50">
                    <ul className="space-y-2">
                      {service.bulletPoints.split(', ').slice(0, 3).map((point, idx) => <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                          <ChevronRight className="size-3 text-secondary" />
                          {point}
                        </li>)}
                    </ul>
                  </div>

                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-secondary group-hover:w-full transition-all duration-500 rounded-b-lg" />
                </div>;
          })}
          </div>

          <div className={`flex flex-col md:flex-row items-center justify-between gap-8 p-8 md:p-12 rounded-lg bg-primary text-primary-foreground transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="max-w-2xl text-center md:text-left">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">Ready to start your next industrial project?</h3>
              <p className="text-primary-foreground/80 text-lg">
                Our team of experts is ready to provide the precision and quality your facility demands.
              </p>
            </div>
            <Button size="lg" variant="secondary" className="text-lg px-8 py-6 h-auto font-bold" asChild>
              
            </Button>
          </div>
        </div>
      </div>
    </section>;
}